import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { updateDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { updateTechPrepUser, deleteUser } from "../../config/firebase";
import { splitName, joinName } from "../../utils/name";
import ModalDialogTitle from "../common/ModalDialogTitle";
import ModalActions from "../common/ModalActions";

const EditTpUser = ({ open, onClose, user, onSuccess, onUserDeleted }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [userTypes, setUserTypes] = useState({
    staff: false,
    coordinator: false,
    instructor: false,
    admin: false,
    pathwayManager: false,
  });
  const [partner, setPartner] = useState("");
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [active, setActive] = useState("-1"); // -1 = active, 0 = inactive
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Coordinators are tied to a Tech Prep Partner org, so the dropdown only
  // offers partners currently marked active in the Partners collection.
  useEffect(() => {
    const loadPartners = async () => {
      try {
        const snap = await getDocs(collection(db, "TpPartners"));
        const names = snap.docs
          .map((d) => d.data())
          .filter((p) => p.Active_yn === "y" && p.Tech_Prep_Partner)
          .map((p) => p.Tech_Prep_Partner)
          .sort((a, b) => a.localeCompare(b));
        setPartnerOptions(names);
      } catch (e) {
        console.error("Error loading partners:", e);
        setPartnerOptions([]);
      }
    };
    loadPartners();
  }, []);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, "");

    // Format as (xxx) xxx-xxxx
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  useEffect(() => {
    if (user) {
      if (user.firstName || user.lastName) {
        setFirstName(user.firstName || "");
        setLastName(user.lastName || "");
      } else {
        const split = splitName(user.name);
        setFirstName(split.firstName);
        setLastName(split.lastName);
      }
      setEmail(user.email);
      setPhone(user.phone ? formatPhoneNumber(user.phone) : "");
      setUserTypes({
        staff: user.userTypes?.staff || false,
        coordinator: user.userTypes?.coordinator || false,
        instructor: user.userTypes?.instructor || false,
        // Check both legacy flag and new userTypes structure for admin
        admin: user.userTypes?.admin || user.techPrepAdminFlag || false,
        pathwayManager: user.userTypes?.pathwayManager || false,
      });
      setPartner(user.tp_partner || "");
      // Initialize active flag (-1 active, 0 inactive). Missing Active/active
      // fields count as active, since most existing user docs predate this
      // field (same convention as EditTpInstructor).
      setActive(
        user.Active !== undefined
          ? String(user.Active)
          : user.active !== undefined
            ? String(user.active)
            : "-1",
      );
    }
  }, [user]);

  const handleUserTypeChange = (type) => (e) => {
    setUserTypes((prev) => ({ ...prev, [type]: e.target.checked }));
  };

  const handleActiveToggle = (e) => {
    setActive(e.target.checked ? "-1" : "0");
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Use the new updateTechPrepUser function that updates both Auth and Firestore
      const result = await updateTechPrepUser({
        uid: user.id,
        firstName,
        lastName,
        name: joinName(firstName, lastName),
        email,
        phone,
        userTypes,
        techPrepAdminFlag: userTypes.admin,
        tp_partner: userTypes.coordinator ? partner : "",
        Active: active,
      });

      if (result.success) {
        if (onSuccess) onSuccess();
        onClose();
      } else {
        console.error("Error updating user:", result.message);
        alert(`Error updating user: ${result.message}`);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert(`Error updating user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone."
      )
    ) {
      setDeleteLoading(true);
      try {
        // deleteUser removes both the Firebase Auth account and the
        // Firestore users doc (a plain deleteDoc here only removed the
        // Firestore doc, leaving the Auth account behind and blocking
        // re-creating a user with that same email).
        const result = await deleteUser(user.id);
        if (!result.success) {
          throw new Error(result.message || "Error deleting user");
        }
        if (onUserDeleted) onUserDeleted();
        onClose();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(error.message || "Error deleting user. Please try again.");
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <ModalDialogTitle onClose={onClose}>Edit User</ModalDialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="First Name"
          type="text"
          fullWidth
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Last Name"
          type="text"
          fullWidth
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Phone"
          type="text"
          fullWidth
          value={phone}
          onChange={handlePhoneChange}
          placeholder="(xxx) xxx-xxxx"
          inputProps={{ maxLength: 14 }}
        />
        {userTypes.coordinator && (
          <FormControl fullWidth margin="dense">
            <InputLabel>Partner</InputLabel>
            <Select
              value={partner}
              onChange={(e) => setPartner(e.target.value)}
              label="Partner"
            >
              {partnerOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={userTypes.staff}
              onChange={handleUserTypeChange("staff")}
            />
          }
          label="Staff"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={userTypes.coordinator}
              onChange={handleUserTypeChange("coordinator")}
            />
          }
          label="Coordinator"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={userTypes.instructor}
              onChange={handleUserTypeChange("instructor")}
            />
          }
          label="Instructor"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={userTypes.admin}
              onChange={handleUserTypeChange("admin")}
            />
          }
          label="Admin"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={userTypes.pathwayManager}
              onChange={handleUserTypeChange("pathwayManager")}
            />
          }
          label="Pathway Manager"
        />
        <FormControlLabel
          control={
            <Switch
              checked={String(active) === "-1"}
              onChange={handleActiveToggle}
              color="primary"
            />
          }
          label={String(active) === "-1" ? "Active" : "Inactive"}
        />
      </DialogContent>
      <ModalActions
        onSave={handleSubmit}
        saving={loading}
        onDelete={handleDelete}
        deleting={deleteLoading}
        saveLabel="Save"
      />
    </Dialog>
  );
};

export default EditTpUser;
